import { Button, Flex, Group, Stack, Text, ThemeIcon, Title, UnstyledButton } from "@mantine/core";
import { IConnectedIssueMinInfo, IIssueApi } from "@schemas/issueSchema";
import { IconArrowDown, IconArrowUp, IconCornerRightDown, IconPlus } from "@tabler/icons-react";
import React from "react";
import {
  useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery,
  useGetProjectByProjectIdIssuesTrpcIssueGetConnectedIssuesQuery,
} from "../../../openapi";
import { ISSUE_STATES_OPTIONS } from "../../app/routes/issuesApp/issueConstants";
import { addAppModalThunk } from "../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { AppModalEnums } from "../../mc-shared/sharedConstants";
import FailedAlert from "../FailedAlert";
import { ConnectIssueModal } from "./IssueConnectIssueModal";

// ---- Types that mirror your Zod schemas (for the UI layer) ----
export type IssueStatus = "Fullført" | "I prosess" | "På hold" | "Opprettet" | "Utgår";

function StatusDot({ status, size = 10 }: { status: IssueStatus; size?: number }) {
  const { color } = ISSUE_STATES_OPTIONS.find((s) => s.value === status) ?? { color: "gray" };
  return (
    <ThemeIcon size={size} radius="xl" color={color} variant="filled" style={{ minWidth: size, minHeight: size }} />
  );
}

function ConnectedRow({ item, onSelect }: { item: IConnectedIssueMinInfo; onSelect?: (id: string) => void }) {
  const content = (
    <Group justify="space-between" wrap="nowrap">
      <Group gap={6} wrap="nowrap" justify="center" align="center">
        <StatusDot status={item.status} />
        <Text size="xs" c="dimmed" fw={700}>
          {item.id}
        </Text>
        <Text size="sm" fw={500} lineClamp={1}>
          {item.title}
        </Text>
      </Group>
    </Group>
  );

  return (
    <UnstyledButton onClick={() => onSelect?.(item._id)} aria-label={`Open ${item.title}`} w="100%" p="xs">
      {content}
    </UnstyledButton>
  );
}

function ConnectionSection({
  title,
  icon,
  items,
  onSelect,
}: {
  title: string;
  icon: React.ReactNode;
  items: IConnectedIssueMinInfo[];
  onSelect?: (id: string) => void;
}) {
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          {icon}
          <Title order={6}>{title}</Title>
        </Group>
      </Group>

      <Stack gap={0}>
        {items.map((it) => (
          <ConnectedRow key={it._id} item={it} onSelect={onSelect} />
        ))}
      </Stack>
    </Stack>
  );
}

// ---- Main component (vertical: parents above, children below; only render sections with items) ----
export type IssueConnectionsProps = {
  issue: IIssueApi;
  boardId: string;
  projectId: string;
};

export function IssueConnectedIssuesSection({ issue, boardId, projectId }: IssueConnectionsProps) {
  const dispatch = useAppDispatch();
  const [connectIssueModalOpened, setConnectIssueModalOpened] = React.useState(false);

  const { data, isError } = useGetProjectByProjectIdIssuesTrpcIssueGetConnectedIssuesQuery({
    projectId,
    _id: issue._id,
  });

  const {
    data: otherBoardsData,
    isFetching: isOtherBoardsFetching,
    isError: isOtherBoardsError,
  } = useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery({
    issueId: issue._id,
    projectId,
  });

  const { parents, children } = data ?? { parents: [], children: [] };
  const connectedFromOtherBoards =
    otherBoardsData?.map((c) => {
      return {
        _id: c._id,
        id: c.id,
        title: `${c.issueBoard.name} - ${c.title}`,
        status: c.status,
      };
    }) ?? [];

  const hasParents = Array.isArray(parents) && parents.length > 0;
  const hasChildren = Array.isArray(children) && children.length > 0;
  const hasOtherBoardsConnections = Array.isArray(otherBoardsData) && otherBoardsData.length > 0;

  const useMantineModal = useAppSelector((state) => state.issueReducerNew.useMantineModal);

  return (
    <Stack gap="sm">
      {isError === true && <FailedAlert errorMessage={<>Feil ved lasting av tilknyttede saker</>} />}
      {hasParents !== false && (
        <ConnectionSection
          // Wrong title due to data structure mistake
          title="Koblet til"
          icon={
            <ThemeIcon radius="xl" variant="light" color="cyan">
              <IconArrowUp size={16} />
            </ThemeIcon>
          }
          items={parents}
          onSelect={(id) =>
            dispatch(
              addAppModalThunk({
                itemId: id,
                boardId: boardId,
                projectId,
                app: AppModalEnums.ISSUE,
              }),
            )
          }
        />
      )}

      {hasChildren !== false && (
        <ConnectionSection
          // Wrong title due to data structure mistake
          title="Koblet fra"
          icon={
            <ThemeIcon size={26} radius="xl" variant="light" color="teal">
              <IconArrowDown size={16} />
            </ThemeIcon>
          }
          items={children}
          onSelect={(id) =>
            dispatch(
              addAppModalThunk({
                itemId: id,
                boardId: boardId,
                projectId,
                app: AppModalEnums.ISSUE,
              }),
            )
          }
        />
      )}

      {isOtherBoardsError === true && (
        <FailedAlert errorMessage={<>Feil ved lasting av tilkoblinger fra andre registre</>} />
      )}
      {hasOtherBoardsConnections !== false && (
        <ConnectionSection
          title="Koblet fra andre registre"
          icon={
            <ThemeIcon radius="xl" variant="light" color="indigo">
              <IconCornerRightDown size={16} />
            </ThemeIcon>
          }
          items={connectedFromOtherBoards}
          onSelect={(id) =>
            dispatch(
              addAppModalThunk({
                itemId: id,
                boardId: boardId,
                projectId,
                app: AppModalEnums.ISSUE,
              }),
            )
          }
        />
      )}

      <Flex w="100%" justify="flex-end">
        <Button variant="outline" onClick={() => setConnectIssueModalOpened(true)} leftSection={<IconPlus size={16} />}>
          Koble sak
        </Button>
      </Flex>
      <ConnectIssueModal
        projectId={projectId}
        boardId={issue?.issueBoard?._id}
        sourceIssueId={issue._id}
        opened={connectIssueModalOpened}
        onClose={() => setConnectIssueModalOpened(false)}
      />
    </Stack>
  );
}
